/**
 * cypAdhdFormKeys.js
 *
 * Single source of truth for CYP ADHD informant form keys on the frontend.
 * Must stay in sync with GUARDIAN_FORM_KEYS / SCHOOL_FORM_KEYS in
 * python-functions/constants/cyp_adhd/registry.py.
 *
 * WHY ROLE MATTERS HERE
 * ---------------------
 * Unlike Adult Autism, where every informant gets the same seven forms, CYP
 * ADHD assigns different form sets depending on who the informant is:
 *
 *   - legalGuardian (parent/carer): all eleven forms, starting with About
 *     you (guardian-only). They are the primary source for developmental,
 *     medical and family history.
 *   - schoolRep (teacher/SENCO): About you (their own variant), Reason
 *     for Referral (their own variant), Education History and SNAP-IV.
 *     A school has no legitimate visibility of a child's family or medical
 *     history, so assigning them those forms would be both useless and a
 *     privacy problem.
 *
 * Anything that counts progress, lists questionnaires, or resolves display
 * tokens must therefore filter by role, not just by module — otherwise a
 * school rep's progress would be averaged over eleven forms they can never
 * see and would never reach 100%.
 *
 * reasonForReferral is a role-VARIANT form: both roles share the key, but the
 * stored form instance carries role-specific wording (chosen server-side at
 * informant creation from the Python registry).
 */

/** Ordered guardian (parent/carer) form keys — matches formNumber 1-11. */
export const CYP_ADHD_GUARDIAN_FORM_KEYS = [
  'aboutYouAndConsent',
  'reasonForReferral',
  'theirEarlyLife',
  'theirFamilyAndHomeLife',
  'theirLifestyle',
  'theirHistoryWithTheLaw',
  'theirFamilyHistory',
  'theirMedicalHistory',
  'theirMentalHealthHistory',
  'educationHistory',
  'snapIV26Informant',
];

/**
 * Clinician-facing form titles, keyed by form key.
 *
 * The stored form titles are personalised for the parent ("About Noah's
 * health") — right on the service-user surface, wrong on the provider's
 * patient overview, where clinicians asked for simple clinical names without
 * the child's name (Tom, 23 Sept standup). Sentence case per the design
 * rules. The parent-facing titles are untouched: this map is read only by
 * provider-side listings.
 */
export const CYP_ADHD_CLINICAL_FORM_TITLES = Object.freeze({
  aboutYouAndConsent: 'Basic information',
  reasonForReferral: 'Reason for referral',
  theirEarlyLife: 'Early life',
  theirFamilyAndHomeLife: 'Family and home life',
  theirLifestyle: 'Daily life',
  theirHistoryWithTheLaw: 'Safety and contact with the law',
  theirFamilyHistory: 'Family history',
  theirMedicalHistory: 'Health',
  theirMentalHealthHistory: 'Mental health',
  educationHistory: 'Education',
  snapIV26Informant: 'SNAP-IV rating scale',
});

/** Ordered school representative form keys — About you leads, then shared. */
export const CYP_ADHD_SCHOOL_FORM_KEYS = [
  'aboutYouAndConsent',
  'reasonForReferral',
  'educationHistory',
  'snapIV26Informant',
];

/** Every CYP ADHD form key, in formNumber order. */
export const CYP_ADHD_ALL_FORM_KEYS = [...CYP_ADHD_GUARDIAN_FORM_KEYS];

/**
 * Resolve the form keys for a CYP ADHD informant by role.
 *
 * Defaults to the guardian set when the role is missing or unrecognised:
 * guardians are the overwhelmingly common case, and showing a guardian their
 * full set is a safe failure mode, whereas defaulting to the school subset
 * would silently hide seven forms from a parent.
 *
 * @param {string} informantRole - 'legalGuardian' | 'schoolRep'
 * @returns {string[]} ordered form keys
 */
export function getCypAdhdFormKeysForRole(informantRole) {
  return informantRole === 'schoolRep'
    ? CYP_ADHD_SCHOOL_FORM_KEYS
    : CYP_ADHD_GUARDIAN_FORM_KEYS;
}
